import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { LogOut, Copy, TrendingUp, Users, IndianRupee, Sparkles, Clock, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import ProfilePhotoUploader from '../components/ProfilePhotoUploader';

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({ contact_name: '', contact_email: '', contact_mobile: '', organization: '', city: '', state: '', intended_role: 'buyer', notes: '' });
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user]);

  const isDSA = profile?.partner_type === 'dsa';

  const fetchData = async () => {
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/partners/me`, { withCredentials: true }),
        axios.get(`${API}/partners/me/referrals`, { withCredentials: true })
      ]);
      setProfile(p.data);
      setReferrals(r.data);
      if (p.data?.partner_type === 'dsa') {
        try {
          const lr = await axios.get(`${API}/partners/dsa/leads`, { withCredentials: true });
          setLeads(lr.data?.items || []);
        } catch (e) { /* leads optional */ }
      }
    } catch (err) {
      toast.error('Failed to load partner data');
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/partners/dsa/leads`, leadForm, { withCredentials: true });
      toast.success('Lead added');
      setShowLeadModal(false);
      setLeadForm({ contact_name: '', contact_email: '', contact_mobile: '', organization: '', city: '', state: '', intended_role: 'buyer', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add lead');
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/partners/dsa/leads/${leadId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Lead marked as ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const copyReferralCode = () => {
    if (!profile?.referral_code) return;
    navigator.clipboard.writeText(profile.referral_code);
    toast.success(`Referral code ${profile.referral_code} copied!`);
  };

  const copyReferralLink = () => {
    if (!profile?.referral_code) return;
    const link = `${window.location.origin}/buyer-register?ref=${profile.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (!user?.approved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md p-8">
          <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Account Pending Approval</h2>
          <p className="text-muted-foreground">Your partner account is under review by Plus Ultra Innovation. You will be notified once approved.</p>
          <button onClick={handleLogout} className="mt-6 px-4 py-2 rounded-md border border-input">Logout</button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div></div>;

  const partnerTypeLabel = profile?.partner_type === 'dsa' ? 'DSA' : 'Referral Partner';
  const outstanding = (profile?.total_commission_earned || 0) - (profile?.total_commission_paid || 0);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-6 w-6 text-gold" />
            <h1 className="text-xl font-bold">{partnerTypeLabel} Dashboard</h1>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-white/70">{user?.email}</span>
            <button onClick={handleLogout} className="px-3 py-1.5 rounded-md border border-white/30 text-sm" data-testid="logout-btn">
              <LogOut className="inline h-4 w-4 mr-1" /> Logout
            </button>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Profile Photo + Referral code card */}
        <div className="p-6 rounded-lg bg-card border border-border mb-6">
          <ProfilePhotoUploader
            currentUrl={profile?.profile_photo_url || user?.profile_photo_url}
            label="Profile Photo"
            onUploaded={(url) => setProfile({ ...(profile || {}), profile_photo_url: url })}
            testid="partner-photo-uploader"
          />
        </div>

        {/* Referral code card */}
        <div className="p-6 rounded-lg bg-navy text-white mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-gold mb-2">Your Referral Code</div>
              <div className="text-4xl font-bold tracking-wider" data-testid="partner-code">{profile?.referral_code}</div>
              <div className="text-sm text-white/70 mt-2">Commission Rate: <span className="text-gold font-semibold">{profile?.commission_rate}%</span></div>
            </div>
            <div className="flex flex-col space-y-2">
              <button onClick={copyReferralCode} className="px-4 py-2 rounded-md bg-gold text-navy font-semibold" data-testid="copy-code-btn">
                <Copy className="inline h-4 w-4 mr-2" /> Copy Code
              </button>
              <button onClick={copyReferralLink} className="px-4 py-2 rounded-md bg-orange text-white font-semibold" data-testid="copy-link-btn">
                <Copy className="inline h-4 w-4 mr-2" /> Copy Referral Link
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="p-5 rounded-lg border border-border bg-card">
            <Users className="h-7 w-7 text-navy mb-2" />
            <div className="text-2xl font-bold" data-testid="stat-total-referrals">{profile?.total_referrals || 0}</div>
            <div className="text-xs text-muted-foreground">Total Referrals</div>
          </div>
          <div className="p-5 rounded-lg border border-border bg-card">
            <TrendingUp className="h-7 w-7 text-orange mb-2" />
            <div className="text-2xl font-bold" data-testid="stat-conversions">{profile?.total_conversions || 0}</div>
            <div className="text-xs text-muted-foreground">Conversions</div>
          </div>
          <div className="p-5 rounded-lg border border-border bg-card">
            <IndianRupee className="h-7 w-7 text-gold mb-2" />
            <div className="text-2xl font-bold" data-testid="stat-revenue">₹{(profile?.total_revenue_attributed || 0).toLocaleString('en-IN')}</div>
            <div className="text-xs text-muted-foreground">Revenue Attributed</div>
          </div>
          <div className="p-5 rounded-lg border border-border bg-card">
            <IndianRupee className="h-7 w-7 text-green-600 mb-2" />
            <div className="text-2xl font-bold" data-testid="stat-commission">₹{(profile?.total_commission_earned || 0).toLocaleString('en-IN')}</div>
            <div className="text-xs text-muted-foreground">Commission Earned</div>
          </div>
        </div>

        {/* Outstanding */}
        <div className="p-5 rounded-lg border-2 border-gold bg-gold/5 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-navy font-semibold">Outstanding Commission</div>
              <div className="text-3xl font-bold text-navy">₹{outstanding.toLocaleString('en-IN')}</div>
            </div>
            <div className="text-xs text-muted-foreground text-right">
              Paid: ₹{(profile?.total_commission_paid || 0).toLocaleString('en-IN')}<br/>
              Earned: ₹{(profile?.total_commission_earned || 0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>

        {/* Referrals Table */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold text-navy mb-4">Your Referrals ({referrals.length})</h2>
          {referrals.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No referrals yet. Share your code <strong className="text-navy">{profile?.referral_code}</strong> to start earning!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border">
                  <th className="text-left py-2 px-3 font-medium">Referred User</th>
                  <th className="text-left py-2 px-3 font-medium">Role</th>
                  <th className="text-left py-2 px-3 font-medium">Status</th>
                  <th className="text-right py-2 px-3 font-medium">Revenue</th>
                  <th className="text-right py-2 px-3 font-medium">Commission</th>
                  <th className="text-left py-2 px-3 font-medium">Date</th>
                </tr></thead>
                <tbody>
                  {referrals.map(r => (
                    <tr key={r.id} className="border-b border-border" data-testid={`ref-row-${r.id}`}>
                      <td className="py-2 px-3">
                        <div>{r.referred_user?.name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{r.referred_user?.email}</div>
                      </td>
                      <td className="py-2 px-3 capitalize">{r.referred_user_role}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.conversion_status === 'subscribed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {r.conversion_status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right">{r.revenue_attributed ? `₹${r.revenue_attributed.toLocaleString('en-IN')}` : '—'}</td>
                      <td className="py-2 px-3 text-right">{r.commission_amount ? `₹${r.commission_amount.toLocaleString('en-IN')}` : '—'}</td>
                      <td className="py-2 px-3 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* DSA Leads Section */}
        {isDSA && (
          <div className="bg-card border border-border rounded-lg p-6 mt-6" data-testid="dsa-leads-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-navy">DSA Leads ({leads.length})</h2>
              <button onClick={() => setShowLeadModal(true)} className="px-4 py-2 rounded bg-orange text-white text-sm font-semibold inline-flex items-center" data-testid="add-lead-btn">
                <UserPlus className="h-4 w-4 mr-2" /> Add Lead
              </button>
            </div>
            {leads.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground border border-dashed border-border rounded-lg">
                <UserPlus className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No leads yet. Click "Add Lead" to log your first prospect.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border">
                    <th className="text-left py-2 px-3 font-medium">Contact</th>
                    <th className="text-left py-2 px-3 font-medium">Org / Location</th>
                    <th className="text-left py-2 px-3 font-medium">Intended Role</th>
                    <th className="text-left py-2 px-3 font-medium">Status</th>
                    <th className="text-left py-2 px-3 font-medium">Date</th>
                    <th className="text-left py-2 px-3 font-medium">Actions</th>
                  </tr></thead>
                  <tbody>
                    {leads.map(l => (
                      <tr key={l.id} className="border-b border-border" data-testid={`lead-row-${l.id}`}>
                        <td className="py-2 px-3">
                          <div className="font-medium">{l.contact_name}</div>
                          <div className="text-xs text-muted-foreground">{l.contact_email} {l.contact_mobile && `• ${l.contact_mobile}`}</div>
                        </td>
                        <td className="py-2 px-3 text-xs">{l.organization || '—'}<br/>{l.city}{l.state ? `, ${l.state}` : ''}</td>
                        <td className="py-2 px-3 capitalize text-xs">{l.intended_role || '—'}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${l.status === 'converted' ? 'bg-green-100 text-green-800' : l.status === 'qualified' ? 'bg-navy/10 text-navy' : l.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' : l.status === 'dropped' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                            {l.status}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                        <td className="py-2 px-3">
                          <select value={l.status} onChange={(e) => updateLeadStatus(l.id, e.target.value)} className="text-xs px-2 py-1 rounded border" data-testid={`lead-status-${l.id}`}>
                            <option value="new">new</option>
                            <option value="contacted">contacted</option>
                            <option value="qualified">qualified</option>
                            <option value="converted">converted</option>
                            <option value="dropped">dropped</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Lead Modal */}
      {showLeadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="lead-modal">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-bold text-navy">Add DSA Lead</h3>
              <button onClick={() => setShowLeadModal(false)} className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submitLead} className="space-y-3">
              <input required placeholder="Contact Name *" value={leadForm.contact_name} onChange={e => setLeadForm({ ...leadForm, contact_name: e.target.value })} className="w-full px-3 py-2 border rounded" data-testid="lead-name-input" />
              <input type="email" placeholder="Email" value={leadForm.contact_email} onChange={e => setLeadForm({ ...leadForm, contact_email: e.target.value })} className="w-full px-3 py-2 border rounded" />
              <input placeholder="Mobile" value={leadForm.contact_mobile} onChange={e => setLeadForm({ ...leadForm, contact_mobile: e.target.value })} className="w-full px-3 py-2 border rounded" />
              <input placeholder="Organization / Society" value={leadForm.organization} onChange={e => setLeadForm({ ...leadForm, organization: e.target.value })} className="w-full px-3 py-2 border rounded" />
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="City" value={leadForm.city} onChange={e => setLeadForm({ ...leadForm, city: e.target.value })} className="w-full px-3 py-2 border rounded" />
                <input placeholder="State" value={leadForm.state} onChange={e => setLeadForm({ ...leadForm, state: e.target.value })} className="w-full px-3 py-2 border rounded" />
              </div>
              <select value={leadForm.intended_role} onChange={e => setLeadForm({ ...leadForm, intended_role: e.target.value })} className="w-full px-3 py-2 border rounded">
                <option value="buyer">Intended: Buyer</option>
                <option value="supplier">Intended: Supplier</option>
              </select>
              <textarea placeholder="Notes" rows={2} value={leadForm.notes} onChange={e => setLeadForm({ ...leadForm, notes: e.target.value })} className="w-full px-3 py-2 border rounded" />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowLeadModal(false)} className="px-4 py-2 rounded border text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 rounded bg-navy text-white text-sm font-semibold" data-testid="submit-lead-btn">Save Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerDashboard;
